import { z } from 'zod';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_FILE_TYPES = [
  // Image types
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",

  // Document types
  "application/msword", // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.ms-excel", // .xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "text/csv", // .csv
  "application/pdf" // .pdf
];

export const step1Schema = z
.object({
  name: z
    .string()
    .min(3, { message: 'Name must be at least 3 characters long' })
    .max(100, { message: 'Name must be at most 100 characters long' }),
  company_id: z
    .string()
    .nullable(),
  location: z.object({
    name: z
      .string()
      .min(3, { message: 'Shipping location name must be at least 3 characters long' })
      .max(100, { message: 'Shipping location name must be at most 100 characters long' }),
    apartment: z
      .string()
      .min(3, { message: 'Shipping location address must be at least 3 characters long' })
      .max(100, { message: 'Shipping location address must be at most 100 characters long' }),
    billing_apartment: z
      .string()
      .min(3, { message: 'Billing location address must be at least 3 characters long' })
      .max(100, { message: 'Billing location address must be at most 100 characters long' }),
  }),
  files: z.array(z.object({
    file: z.instanceof(File).superRefine((file, ctx) => {
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid file type. Only .jpg, .jpeg, .png, .webp, .svg, .doc, .docx, .xls, .xlsx, .csv, and .pdf formats are supported.",
          params: { type: "invalid_type" },
        });
      }

      if (file.size > MAX_FILE_SIZE) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Each file must be less than 10MB.",
          params: { type: "too_large" },
        });
      }
    })
  }))
})

export const step2Schema = z.
array(
  z.object({
    contact_fname: z
      .string()
      .min(3, { message: 'Contact first name must be at least 3 characters long' })
      .max(100, { message: 'Contact first name must be at most 100 characters long' }),
    contact_lname: z
      .string()
      .min(3, { message: 'Contact last name must be at least 3 characters long' })
      .max(100, { message: 'Contact last name must be at most 100 characters long' }),
    email: z
      .string()
      .email({ message: "Invalid email address" }),
    phone: z.union([
      z.string()
        .regex(/^\d+$/, { message: 'Phone number must contain only digits' })
        .min(10, { message: 'Phone number is too short' })
        .max(15, { message: 'Phone number is too long' }),
      z.literal(''),
      z.null()
    ]).optional(),
    signature: z.union([
        z.string()
          .min(2, { message: 'Signature must be at least 2 characters long' })
          .max(20, { message: 'Signature must be at most 20 characters long' }),
        z.literal(''),
        z.null()
      ]).optional()
  })
)
